import { MinMonthPrices, PriceData, Prices, TravelClass, TravelType } from '../common';
import { EnvConfig } from '../config';
import { SelectionState } from '../store';
import { Indexed, isPresent, sanitize } from '../utils';
import { HttpParam, HttpParams, httpRequest } from './httpUtils';
import { castResponse, CastResponse, Lock, Response } from './types';

interface MonthPrices {
  [dateId: string]: {
    date: string;
    noFlight: boolean;
    price: number;
  };
}

interface PricesResponse {
  currency?: string;
  dep: string;
  dest: string;
  prices: any[];
  availability?: any[];
  weeklyFlights?: any[];
  errorCode?: string;
  errorMessage?: string;
  lowestPriceForMonth?: MonthPrices;
}

const flattenSelections = ({
  locations,
  paxAmount: { adults, c15s, children, infants },
  travelClass,
  directFlights,
  activeLocationIndex,
  locale,
  isCountryShopTestGroup,
}: SelectionState): Indexed<HttpParam> =>
  sanitize({
    departureLocationCode: locations[activeLocationIndex]?.origin?.locationCode,
    destinationLocationCode: locations[activeLocationIndex]?.destination.locationCode,
    travelClass: travelClass === TravelClass.MIXED ? undefined : travelClass,
    adults: adults + c15s,
    children,
    infants,
    directFlights,
    locale,
    isCountryShopTestGroup,
  });

function populateMonths(lowestPriceForMonth: MonthPrices, numberOfMonths: number) {
  const now = new Date();

  return Array.from(Array(numberOfMonths)).reduce((acc, _key, currentIndex) => {
    const current = new Date(now.getFullYear(), now.getMonth() + currentIndex, 15);
    const isoCurrentDate = current.toISOString();
    const date = isoCurrentDate.substring(0, 7);

    if (date in lowestPriceForMonth) {
      acc[date] = lowestPriceForMonth[date];
    } else {
      acc[date] = {
        noFlight: true,
        date: isoCurrentDate.substring(0, 10),
        price: 0,
      };
    }

    return acc;
  }, {});
}

const asPriceDataResponse = ({ status, payload }: Response<PricesResponse>): Response<PriceData> => {
  const { availability, currency, errorCode, prices, weeklyFlights, lowestPriceForMonth } = payload;
  const response: Response<PriceData> = {
    status,
    payload: {
      availability,
      currency,
      errorCode,
      weeklyFlights,
    },
  };
  if (isPresent(prices)) {
    response.payload.prices = prices.reduce((acc: Prices, { date, price }: any) => {
      acc[date] = {
        currencyCode: currency,
        amount: isPresent(price) ? String(price) : price,
      };
      return acc;
    }, {});

    if (Object.keys(lowestPriceForMonth).length !== 0) {
      const numberOfMonths = prices.reduce((months: string[], { date }: any) => {
        const month = date.substring(0, 7);
        if (!months.includes(month)) {
          months.push(month);
        }
        return months;
      }, []).length;

      const populatedMonths = populateMonths(lowestPriceForMonth, numberOfMonths);

      let minPriceForYear = Infinity;

      response.payload.lowestPriceForMonth = Object.keys(populatedMonths || {}).reduce(
        (acc: MinMonthPrices, key: string) => {
          const amount = isPresent(populatedMonths[key].price)
            ? String(populatedMonths[key].price)
            : populatedMonths[key].price;

          if (+amount !== 0 && minPriceForYear >= +amount) {
            minPriceForYear = +amount;
          }

          acc[key] = {
            ...populatedMonths[key],
            price: {
              currencyCode: currency,
              amount,
            },
          };

          return acc;
        },
        {}
      );
      response.payload.minPriceForYear = minPriceForYear;
    }
  }
  return response;
};

const getPricesForFullYearLock: Lock = new Lock();
const getPricesForFollowingMonthLock: Lock = new Lock();
export const cancelGetPricesForFollowingMonth = (): boolean => getPricesForFollowingMonthLock.cancel();

export const getPricesForFullYear = (
  { instantSearchUrl }: EnvConfig,
  selection: SelectionState,
  startDate: string
): Promise<Response<PriceData>> => {
  getPricesForFullYearLock.cancel();

  const params: HttpParams = new HttpParams(flattenSelections(selection))
    .set('numberOfDays', 360)
    .set('startDate', startDate)
    .set('oneway', selection.travelType === TravelType.ONE_WAY);
  return httpRequest<PricesResponse>({
    method: 'GET',
    url: `${instantSearchUrl}/pricesforperiod`,
    params,
    lock: getPricesForFullYearLock,
    timeout: 6000,
    retries: 2,
  })
    .then(asPriceDataResponse)
    .catch(castResponse as CastResponse<PriceData>)
    .finally(() => getPricesForFullYearLock.release());
};

export const getPricesForFollowingMonth = (
  { instantSearchUrl }: EnvConfig,
  selection: SelectionState
): Promise<Response<PriceData>> => {
  getPricesForFollowingMonthLock.cancel();

  const params: HttpParams = new HttpParams(flattenSelections(selection))
    .set('numberOfDays', 30)
    .set('departureDate', selection.travelDates.departureDate);
  return httpRequest<PricesResponse>({
    method: 'GET',
    url: `${instantSearchUrl}/pricesforperiod/fixeddeparture`,
    params,
    lock: getPricesForFollowingMonthLock,
    timeout: 6000,
    retries: 2,
  })
    .then(asPriceDataResponse)
    .catch(castResponse as CastResponse<PriceData>)
    .finally(() => getPricesForFollowingMonthLock.release());
};
